
import {
  Component, Prop, Vue,
} from 'nuxt-property-decorator'
import IImageVideoContent from '../shared/general/interfaces/IImageVideoContent'
import { openLightbox, openLightboxCarousel } from '../shared/general/services/LightboxService'
import { IGalleryContent } from '../shared/general/interfaces/IGalleryContent'
import BaseHeadline from './base/BaseHeadline.vue'

@Component({
  name: 'Gallery',
  components: {
    BaseHeadline,
    CarouselControls: () => import('./carousel/CarouselControls.vue'),
    BaseImageVideo: () => import('./ImageVideo/BaseImageVideo.vue'),
    BaseIcon: () => import('./base/BaseIcon.vue'),
  },
})
export default class Gallery extends Vue {
  @Prop() headline ?: string

  @Prop({ required: true }) elements! : IGalleryContent[]

  @Prop({ default: false }) lightbox! : boolean

  $refs! : {
    carousel ?: HTMLUListElement
    textbox ?: HTMLDivElement
  }

  private showControls : boolean = true

  private currentSlide : number = 0

  private slideSizes : string[] = [
    'aspect-4/3 lg:aspect-video flex-[1_0_calc(100%-48px)] sm:flex-[1_1_408px] md:flex-[1_1_560px] lg:flex-[0_0_840px]',
    'flex-[1_0_160px] sm:flex-[1_0_120px] md:flex-[1_0_122px] lg:flex-[0_0_192px]',
    'flex-[1_0_160px] sm:flex-[1_0_120px] md:flex-[1_0_122px] lg:flex-[0_0_192px]',
  ]

  private swipeStart : number = 0

  mounted () {
    this.showControls = (this.$refs.carousel?.children.length ?? 0) > 1
  }

  private async moveToSlide (index : number) : Promise<void> {
    this.currentSlide = this.clampRotate(index, 0, this.elements.length - 1)
  }

  private startSwipe (event : TouchEvent) {
    this.swipeStart = event.touches[0].clientX
  }

  private endSwipe (event : TouchEvent) {
    const galleryWrapper = event.currentTarget as HTMLElement
    galleryWrapper.style.transform = 'translateX(0px)'
    this.swipeStart = 0
  }

  private swipe (event : TouchEvent) {
    if (this.swipeStart === 0) { return }

    const currentTouch = event.touches[0].clientX
    const swipeDelta = currentTouch - this.swipeStart

    const galleryWrapper = event.currentTarget as HTMLElement
    galleryWrapper.style.transform = `translateX(${swipeDelta}px)`

    // 20% of the first images width required for swiping to next/last slide
    const swipeThreshold = this.$refs.carousel!.children[0].clientWidth / 5

    if (swipeDelta > swipeThreshold) {
      this.moveToSlide(this.currentSlide - 1)
      this.swipeStart = currentTouch
    } else if (swipeDelta < -swipeThreshold) {
      this.moveToSlide(this.currentSlide + 1)
      this.swipeStart = currentTouch
    }
  }

  private getElementForIndex (index : number) : IGalleryContent {
    const useIndex = this.clampRotate((index + this.currentSlide) - 1, 0, this.elements.length - 1)
    return this.elements[useIndex]
  }

  private get currentItem () : IGalleryContent {
    return this.elements[this.currentSlide]
  }

  private get currentContent () : IImageVideoContent {
    if (this.currentItem.mediaType === 'image') {
      return {
        tag: 'BasePicture',
        props: { image: this.currentItem.image },
      }
    }

    if (!this.currentItem.video?.id?.length || this.currentItem.video.format === 'html5') {
      return {
        tag: 'BaseHtmlPlayer',
        props: {
          htmlPlayerElements: this.currentItem.video?.htmlPlayerElements,
        },
      }
    }

    return {
      tag: 'BaseVideo',
      props: {
        id: this.currentItem.video.id,
        title: this.currentItem.video.title,
        format: this.currentItem.video.format,
        privacyHash: this.currentItem.video.privacyHash,
      },
    }
  }

  private handleClick (index : number) : void {
    if (index > 1) {
      this.moveToSlide((this.currentSlide + index) - 1)
      return
    }

    this.showLightboxCarousel(
      this.currentContent,
      this.currentItem.mediaType,
      this.currentSlide,
    )
  }

  private showLightboxCarousel (
    content : IImageVideoContent,
    mediaType : 'image' | 'video',
    startIndex : number,
  ) : void {
    if (!this.lightbox && mediaType === 'video') {
      openLightbox(content)
      return
    }

    if (!this.lightbox) return

    openLightboxCarousel(this.elements, startIndex)
  }

  /**
   * Acts like clamp, but rotates to min/max if necessary.
   * clamp(6, 1, 4) => 4
   * clampRotate(6, 1, 4) => 2
   * @param value Given value to rotate
   * @param min Minimum value
   * @param max Maximum value
   * @private
   */
  private clampRotate (value : number, min : number, max : number) : number {
    let useMin = min
    let useMax = max
    if (min > max) [useMin, useMax] = [max, min]

    const range = useMax - useMin + 1

    if (value < useMin) return useMax - ((useMin - value - 1) % range)
    if (value > useMax) return useMin + ((value - useMax - 1) % range)
    return value
  }
}
